import type {
  SignatureCompletion,
  SignatureCompletionStatus,
  WalaSignature,
} from '../planningView/widgets/AssignmentList/ics204SignatureSlots';

export const TASK_REQUEST_EXECUTE_ROLE = 'Request Approval to Execute';
export const TASK_APPROVE_EXECUTE_GS_ROLE =
  'Approve Execution — Group Supervisor';
export const TASK_APPROVE_EXECUTE_BD_ROLE =
  'Approve Execution — Branch Director';
/** Pre-dual-approve role string; rewritten by migrateLegacyTaskSignatures. */
export const TASK_LEGACY_APPROVE_EXECUTE_ROLE = 'Approve Execution';
export const TASK_REQUEST_COMPLETION_ROLE = 'Request Completion Approval';
export const TASK_APPROVE_COMPLETION_ROLE = 'Approve Completion';

export type TaskSignatureSlotId =
  | 'request-execute'
  | 'approve-execute-gs'
  | 'approve-execute-bd'
  | 'request-completion'
  | 'approve-completion';

export type TaskSignatureSlot = {
  id: TaskSignatureSlotId;
  label: string;
  role: string;
  /** Index in the signatures array (0–4). */
  index: number;
  /** Request slots auto-stamp; approve slots require typed name. */
  requiresTypedName: boolean;
};

export const TASK_SIGNATURE_SLOTS: TaskSignatureSlot[] = [
  {
    id: 'request-execute',
    label: TASK_REQUEST_EXECUTE_ROLE,
    role: TASK_REQUEST_EXECUTE_ROLE,
    index: 0,
    requiresTypedName: false,
  },
  {
    id: 'approve-execute-gs',
    label: TASK_APPROVE_EXECUTE_GS_ROLE,
    role: TASK_APPROVE_EXECUTE_GS_ROLE,
    index: 1,
    requiresTypedName: true,
  },
  {
    id: 'approve-execute-bd',
    label: TASK_APPROVE_EXECUTE_BD_ROLE,
    role: TASK_APPROVE_EXECUTE_BD_ROLE,
    index: 2,
    requiresTypedName: true,
  },
  {
    id: 'request-completion',
    label: TASK_REQUEST_COMPLETION_ROLE,
    role: TASK_REQUEST_COMPLETION_ROLE,
    index: 3,
    requiresTypedName: false,
  },
  {
    id: 'approve-completion',
    label: TASK_APPROVE_COMPLETION_ROLE,
    role: TASK_APPROVE_COMPLETION_ROLE,
    index: 4,
    requiresTypedName: true,
  },
];

export const TASK_REQUIRED_SIGNATURE_TOTAL = TASK_SIGNATURE_SLOTS.length;
export const TASK_EXECUTE_APPROVED_SIGNATURE_COUNT = 3;

/**
 * Rewrites the old single Approve Execution stamp to Group Supervisor, and
 * inserts a Branch Director stamp (cloned from GS) when the chain already
 * advanced to Request Completion under the 4-slot model.
 */
export function migrateLegacyTaskSignatures(
  signatures: WalaSignature[]
): WalaSignature[] {
  const remapped = signatures.map((stamp) =>
    stamp.role === TASK_LEGACY_APPROVE_EXECUTE_ROLE
      ? { ...stamp, role: TASK_APPROVE_EXECUTE_GS_ROLE }
      : stamp
  );

  if (
    remapped.length >= 3 &&
    remapped[1]?.role === TASK_APPROVE_EXECUTE_GS_ROLE &&
    remapped[2]?.role === TASK_REQUEST_COMPLETION_ROLE
  ) {
    const gs = remapped[1];
    const bd: WalaSignature = {
      name: gs.name,
      role: TASK_APPROVE_EXECUTE_BD_ROLE,
      signedAt: gs.signedAt,
      ...(gs.userGuid ? { userGuid: gs.userGuid } : {}),
      ...(gs.positionTitle ? { positionTitle: gs.positionTitle } : {}),
    };
    return [remapped[0], remapped[1], bd, ...remapped.slice(2)];
  }

  return remapped;
}

export function parseTaskSignatures(value: unknown): WalaSignature[] {
  if (!Array.isArray(value)) return [];
  const parsed = value.filter(
    (entry): entry is WalaSignature =>
      !!entry &&
      typeof entry === 'object' &&
      typeof (entry as WalaSignature).name === 'string' &&
      typeof (entry as WalaSignature).role === 'string' &&
      typeof (entry as WalaSignature).signedAt === 'number' &&
      (entry as WalaSignature).name.trim().length > 0 &&
      (entry as WalaSignature).role.trim().length > 0
  );
  return migrateLegacyTaskSignatures(parsed);
}

export function getTaskSignatureCompletion(
  signatures: WalaSignature[] | null | undefined
): SignatureCompletion {
  const safe = signatures ?? [];
  const requiredTotal = TASK_REQUIRED_SIGNATURE_TOTAL;
  const slots = TASK_SIGNATURE_SLOTS.map((slot) => ({
    id: slot.id,
    label: slot.label,
    complete: safe.length > slot.index,
  }));
  const completedCount = slots.filter((slot) => slot.complete).length;

  let status: SignatureCompletionStatus = 'draft';
  if (completedCount === 0) {
    status = 'draft';
  } else if (completedCount >= requiredTotal) {
    status = 'signed';
  } else {
    status = 'partially-signed';
  }

  return {
    status,
    completedCount: Math.min(completedCount, requiredTotal),
    requiredTotal,
    percent:
      requiredTotal === 0
        ? 0
        : Math.round(
            (Math.min(completedCount, requiredTotal) / requiredTotal) * 100
          ),
    slots,
  };
}

export function nextTaskSignatureSlot(
  signatures: WalaSignature[] | null | undefined
): TaskSignatureSlot | null {
  const count = (signatures ?? []).length;
  return TASK_SIGNATURE_SLOTS[count] ?? null;
}

export type TaskApprovalCtaKind = TaskSignatureSlotId | 'begin-execute';

export type TaskApprovalCta = {
  kind: TaskApprovalCtaKind;
  label: string;
  /** Null for Begin Execution (status-only, no signature stamp). */
  slot: TaskSignatureSlot | null;
};

export const TASK_BEGIN_EXECUTE_LABEL = 'Begin Execution';

export function isApproveExecuteCtaKind(
  kind: TaskApprovalCtaKind | null | undefined
): boolean {
  return kind === 'approve-execute-gs' || kind === 'approve-execute-bd';
}

/**
 * Banner CTA from signature count + tip status.
 * After three signatures: Begin Execution until Current/Ongoing, then Request Completion.
 */
export function nextTaskApprovalCta(
  signatures: WalaSignature[] | null | undefined,
  status: string | null | undefined
): TaskApprovalCta | null {
  const count = (signatures ?? []).length;
  if (count === 0) {
    const slot = TASK_SIGNATURE_SLOTS[0];
    return { kind: slot.id, label: slot.label, slot };
  }
  if (count === 1) {
    const slot = TASK_SIGNATURE_SLOTS[1];
    return { kind: slot.id, label: slot.label, slot };
  }
  if (count === 2) {
    const slot = TASK_SIGNATURE_SLOTS[2];
    return { kind: slot.id, label: slot.label, slot };
  }
  if (count === 3) {
    if (status !== 'Current/Ongoing') {
      return {
        kind: 'begin-execute',
        label: TASK_BEGIN_EXECUTE_LABEL,
        slot: null,
      };
    }
    const slot = TASK_SIGNATURE_SLOTS[3];
    return { kind: slot.id, label: slot.label, slot };
  }
  if (count === 4) {
    const slot = TASK_SIGNATURE_SLOTS[4];
    return { kind: slot.id, label: slot.label, slot };
  }
  return null;
}

export function canSetCurrentOngoing(
  signatures: WalaSignature[] | null | undefined
): boolean {
  return (signatures ?? []).length >= TASK_EXECUTE_APPROVED_SIGNATURE_COUNT;
}

/** Reject Execution while Group Supervisor or Branch Director approval is pending. */
export function canRejectExecution(
  signatures: WalaSignature[] | null | undefined
): boolean {
  const count = (signatures ?? []).length;
  return count === 1 || count === 2;
}

/** Reject Completion sits beside Approve Completion (exactly four signatures). */
export function canRejectCompletion(
  signatures: WalaSignature[] | null | undefined
): boolean {
  return (signatures ?? []).length === 4;
}

/**
 * Reject Completion drops the Request Completion stamp so the tip returns to
 * Request Completion Approval (three execution signatures).
 */
export function signaturesAfterRejectCompletion(
  signatures: WalaSignature[] | null | undefined
): WalaSignature[] {
  return (signatures ?? []).slice(0, TASK_EXECUTE_APPROVED_SIGNATURE_COUNT);
}

export function isTaskFullySigned(
  signatures: WalaSignature[] | null | undefined
): boolean {
  return getTaskSignatureCompletion(signatures).status === 'signed';
}
